import { z } from "zod";

import { BRAND_MARKUP } from "../../constants";
import { ValidationError } from "../../errors";
import { attachment } from "../shared";
import { defineModel } from "../types";

const ASPECT_RATIO_VALUES = [
  "9:16",
  "16:9",
  "1:1",
  "4:3",
  "3:4",
  "3:2",
  "2:3",
  "2:1",
  "1:2",
  "auto",
] as const;

const RESOLUTION_VALUES = ["1k", "2k"] as const;

const ASPECT_RATIO_OPTIONS = ASPECT_RATIO_VALUES.map((value) => ({
  label: value === "auto" ? "Auto" : value,
  value,
}));

const RESOLUTION_OPTIONS = RESOLUTION_VALUES.map((value) => ({
  label: value.toUpperCase(),
  value,
}));

const grokImagineParams = z
  .object({
    prompt: z.string().default(""),
    aspectRatio: z.enum(ASPECT_RATIO_VALUES).default("9:16"),
    resolution: z.enum(RESOLUTION_VALUES).default("1k"),
    attachments: z.array(attachment).optional(),
  })
  .strip();

function buildPayload(
  modelId: "grok-imagine-image" | "grok-imagine-image-pro",
  params: z.infer<typeof grokImagineParams>,
) {
  if (!params?.prompt?.length) {
    throw new ValidationError("Please enter a prompt to generate an image.");
  }

  const imageUrl = params.attachments?.find(
    (att) => att.slot === "reference_image",
  )?.data;

  const effectiveAspectRatio =
    params.aspectRatio === "auto" && !imageUrl ? "1:1" : params.aspectRatio;
  const aspectRatio =
    effectiveAspectRatio === "auto" ? undefined : effectiveAspectRatio;

  const base = {
    model: modelId,
    prompt: params.prompt,
    ...(aspectRatio ? { aspect_ratio: aspectRatio } : {}),
    resolution: params.resolution,
    response_format: "url" as const,
  };

  if (imageUrl) {
    return {
      ...base,
      image: { url: imageUrl, type: "image_url" as const },
    };
  }

  return base;
}

const STANDARD_COST_PER_IMAGE_USD = 0.02;
const PRO_COST_PER_IMAGE_USD = 0.07;

export const grokImagine = defineModel({
  key: "grok-imagine",
  stepType: "image",
  spec: {
    label: "Grok Imagine",
    description: "Fast image generation and editing by xAI",
    icon: "xai-icon.svg",
    tags: ["image-gen", "image-edit"],
    featured: true,
    features: {
      aspectRatio: true,
      resolution: true,
      attachments: true,
      prompts: true,
      variations: true,
    } as const,
    options: {
      aspectRatio: { items: ASPECT_RATIO_OPTIONS },
      resolution: { items: RESOLUTION_OPTIONS },
      attachments: [
        {
          type: "image" as const,
          allowedFormats: ["image/jpeg", "image/png", "image/webp"],
          label: "Reference Image",
          slot: "reference_image",
          required: false,
        },
      ],
    },
    params: grokImagineParams,
  },
  referencePricing: {
    provider: "fal.ai",
    url: "https://fal.ai/models/xai/grok-imagine-image",
    entries: [{ unit: "per_image", unitPriceMicrodollars: 20_000 }],
  },
  providers: [
    {
      provider: "xai",
      modelId: "grok-imagine-image",
      estimateCost() {
        return Math.ceil(
          STANDARD_COST_PER_IMAGE_USD * 1_000_000 * (1 + BRAND_MARKUP),
        );
      },
      toPayload(params) {
        return buildPayload("grok-imagine-image", params);
      },
    },
  ],
});

export const grokImaginePro = defineModel({
  key: "grok-imagine-pro",
  stepType: "image",
  spec: {
    label: "Grok Imagine Pro",
    description: "High-quality image generation and editing by xAI",
    icon: "xai-icon.svg",
    tags: ["image-gen", "image-edit"],
    featured: true,
    features: {
      aspectRatio: true,
      resolution: true,
      attachments: true,
      prompts: true,
      variations: true,
    } as const,
    options: {
      aspectRatio: { items: ASPECT_RATIO_OPTIONS },
      resolution: { items: RESOLUTION_OPTIONS },
      attachments: [
        {
          type: "image" as const,
          allowedFormats: ["image/jpeg", "image/png", "image/webp"],
          label: "Reference Image",
          slot: "reference_image",
          required: false,
        },
      ],
    },
    params: grokImagineParams,
  },
  referencePricing: {
    provider: "xAI",
    url: "https://docs.x.ai/developers/models",
    entries: [{ unit: "per_image", unitPriceMicrodollars: 70_000 }],
  },
  providers: [
    {
      provider: "xai",
      modelId: "grok-imagine-image-pro",
      estimateCost() {
        return Math.ceil(
          PRO_COST_PER_IMAGE_USD * 1_000_000 * (1 + BRAND_MARKUP),
        );
      },
      toPayload(params) {
        return buildPayload("grok-imagine-image-pro", params);
      },
    },
  ],
});
